import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { ProvinceAction, ProvinceGetter } from '@/store/province/province.module';
import { Province } from '@/store/province/province.model';
import { ProvinceSchema } from '@/store/province/province.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProvinceFormComponent',
  components: {
    BaseModalComponent,
  },
  props: {
    provinceId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.provinceId != null);
    const province = computed<Province | undefined>(() => store.getters[ProvinceGetter.BY_ID](props.provinceId));
    const title = computed(() => isEditMode.value ? 'แก้ไขจังหวัด' : 'เพิ่มจังหวัด');
    const provinceForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name: string;
      englishName: string;
      iso: string;
      isSpecialEconomicZone: boolean;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      englishName: '',
      iso: '',
      isSpecialEconomicZone: false
    });
    const validator = computed(() => ProvinceSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      englishName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'englishName')),
      iso: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'iso'))
    });
    const isValid = computed(() => !errors.name && !errors.englishName && !errors.iso);
    const isLoading = computed<boolean>(() => store.getters[ProvinceGetter.STATUS] === 'init' || store.getters[ProvinceGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.provinceId;
          formData.name = province.value?.name ?? '';
          formData.englishName = province.value?.englishName ?? '';
          formData.iso = province.value?.iso ?? '';
          formData.isSpecialEconomicZone = province.value?.isSpecialEconomicZone ?? false;
        }
        resolve();
      })
      .then(() => provinceForm.value?.show());
    };

    // hide modal
    const hide = () => {
      provinceForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.englishName = '';
        formData.iso = '';
        formData.isSpecialEconomicZone = false;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(ProvinceAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(ProvinceAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(ProvinceAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      provinceForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm
    };
  },
});
